import * as React from "react";
import { NclCodeReaderDialog } from "../common/components.ncl";
import { K2Button } from "./K2Button";
import { AcquireControl, withContext, WithContextPlacementProps } from "./k2hoc";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";
import Quagga, { QuaggaJSResultCallbackFunction, QuaggaJSResultObject } from "@ericblade/quagga2";
import { TReaderCodeType, UpdateHeadered } from "../common/communication.base";
import { createContext, useContext, useEffect, useState } from "react";
import { VCXContext } from "./app";

const mainRule = (props: K2RuleWithVCXProps) => ({
  height: "100%",
  width: "100%",
  backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1),
  flexDirection: "column" as "column",
});

const contentRule = (props: K2RuleWithVCXProps) => ({
  padding: "3px",
  flex: "1 1 auto",
  flexDirection: "column" as "column",
  justifyContent: "flex-start",
  position: "relative" as const,
  "> div": {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    "> canvas": {
      position: "absolute" as const,
      width: "100%",
      height: "100%",
    },
  },
});

const dialogsBtnRule = (props: K2RuleWithVCXProps) => ({
  flex: "0 0 auto",
  flexDirection: "row" as "row",
  height: `${props.vcx.InputControl.getInputHeight(3, true, false)}px`,
});

const partMinRule = (props: K2RuleWithVCXProps) => ({
  flex: "0 1 auto",
  flexDirection: "column" as "column",
});

const partRule = (props: K2RuleWithVCXProps) => ({
  flex: "1",
  maxHeight: `${props.vcx.InputControl.getInputHeight(3, true, false)}px`,
});

const CodeReaderContext = createContext<{ control: NclCodeReaderDialog }>(null);

function initDecoder(ct: TReaderCodeType) {
  let result: Array<string> = [];
  if ((ct & TReaderCodeType.rct2OF0) === TReaderCodeType.rct2OF0) {
    result.push("2of5_reader");
  }
  if ((ct & TReaderCodeType.rctCODABAR) === TReaderCodeType.rctCODABAR) {
    result.push("codabar_reader");
  }
  if ((ct & TReaderCodeType.rctCODE128) === TReaderCodeType.rctCODE128) {
    result.push("code_128_reader");
  }
  if ((ct & TReaderCodeType.rctCODE32) === TReaderCodeType.rctCODE32) {
    result.push("code_32_reader");
  }
  if ((ct & TReaderCodeType.rctCODE39) === TReaderCodeType.rctCODE39) {
    result.push("code_39_reader");
  }
  if ((ct & TReaderCodeType.rctCODE39VIN) === TReaderCodeType.rctCODE39VIN) {
    result.push("code_39_vin_reader");
  }
  if ((ct & TReaderCodeType.rctCODE93) === TReaderCodeType.rctCODE93) {
    result.push("code_93_reader");
  }
  if ((ct & TReaderCodeType.rctEAN) === TReaderCodeType.rctEAN) {
    result.push("ean_reader");
  }
  if ((ct & TReaderCodeType.rctEAN8) === TReaderCodeType.rctEAN8) {
    result.push("ean_8_reader");
  }
  if ((ct & TReaderCodeType.rctI2OF5) === TReaderCodeType.rctI2OF5) {
    result.push("i2of5_reader");
  }
  if ((ct & TReaderCodeType.rctUPC_A) === TReaderCodeType.rctUPC_A) {
    result.push("upc_reader");
  }
  if ((ct & TReaderCodeType.rctUPC_E) === TReaderCodeType.rctUPC_E) {
    result.push("upc_e_reader");
  }
  if (result.length > 0) {
    Quagga.setReaders(result);
  } else {
    alert(`Unknown reader type: ${ct}`);
  }
}

export const K2CodeReader = (props: WithContextPlacementProps) => {
  const control = React.useRef<NclCodeReaderDialog>(
    AcquireControl(props.controlUID, props.vrUID, (ctrl) => ctrl instanceof NclCodeReaderDialog) as NclCodeReaderDialog
  );
  const [dbData, setDbData] = useState(null);
  const [vcxVersion, setVcxVersion] = useState(-1);
  const context = useContext(VCXContext);

  useEffect(() => {
    setDbData(control.current.init({ updateState: updateState, updateVCX: updateVCX }) as UpdateHeadered);

    Quagga.init(
      {
        inputStream: {
          name: "Live",
          type: "LiveStream",
          constraints: {
            width: 900,
            height: 1920,
          },
          area: {
            // defines rectangle of the detection/localization area
            top: "0%", // top offset
            right: "0%", // right offset
            left: "0%", // left offset
            bottom: "0%", // bottom offset
          },
        },
        locator: {
          halfSample: true,
          patchSize: "medium",
        },
        decoder: {
          debug: {
            drawBoundingBox: false,
            showFrequency: false,
            drawScanline: false,
            showPattern: false,
          },
          multiple: false,
        },
      },
      (err) => {
        if (err) {
          console.log(err, "error msg");
        }
        initDecoder(control.current.Ncl.FrgtData.CodeTypes);
        Quagga.start();
        return () => {
          Quagga.stop();
        };
      }
    );

    Quagga.onProcessed(processed);
    Quagga.onDetected(detected);
  }, []);

  useEffect(() => {
    return () => {
      Quagga.offDetected(detected);
      Quagga.offProcessed(processed);
      Quagga.stop();
    };
  }, []);
  const updateState = (data: UpdateHeadered) => {
    setDbData(data);
  };

  const updateVCX = (vcxVersion: number) => {
    setVcxVersion(vcxVersion);
  };

  const processed = (result: QuaggaJSResultObject) => {
    var drawingCtx = Quagga.canvas.ctx.overlay,
      drawingCanvas = Quagga.canvas.dom.overlay;
    if (result) {
      if (result.boxes) {
        drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute("width")), parseInt(drawingCanvas.getAttribute("height")));
      }

      if (result.box) {
        Quagga.ImageDebug.drawPath(
          result.box,
          {
            x: 0,
            y: 1,
          },
          drawingCtx,
          {
            color: "#00F",
            lineWidth: 2,
          }
        );
      }

      if (result.codeResult && result.codeResult.code) {
        Quagga.ImageDebug.drawPath(
          result.line,
          {
            x: "x",
            y: "y",
          },
          drawingCtx,
          {
            color: "red",
            lineWidth: 3,
          }
        );
      }
    }
  };

  const detected = (data: QuaggaJSResultObject) => {
    if (data && data.codeResult && data.codeResult.code) {
      Quagga.offDetected(detected);
      Quagga.stop();
      control.current.setResult(data.codeResult.code);
    }
  };

  return (
    <CodeReaderContext.Provider value={{ control: control.current }}>
      <div className={context.renderer.renderRule(mainRule, { vcx: control.current.VCX })}>
        <div className={context.renderer.renderRule(contentRule, { vcx: control.current.VCX })}>
          <div id="interactive" className="viewport" />
        </div>

        <div className={context.renderer.renderRule(dialogsBtnRule, { vcx: control.current.VCX })}>
          <K2Button controlUID={control.current.OK.MetaData.ControlUID} vrUID={control.current.getRealizerUID()} fillSvg={true} />
          <K2Button controlUID={control.current.CANCEL.MetaData.ControlUID} vrUID={control.current.getRealizerUID()} fillSvg={true} />
        </div>
      </div>
    </CodeReaderContext.Provider>
  );
};
